import React, { useRef, useState } from "react";
import "./FontComponent.css";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  deleteUserFont,
  uploadFontToS3,
} from "../../../../../Redux/actions/projectActions";
import CustomToast, {
  customToatStyle,
} from "../../../../../Components/CustomToast/CustomToast";
import DeleteFontModal from "./DeleteFontModal";
import UploadFontModal from "./UploadFontModal";

const CustomFontUpload = ({ onFontUploaded }) => {
  const dispatch = useDispatch();
  const s3CustomFonts = useSelector(
    (state) => state?.project?.customFonts ?? [],
  );

  const [isOpen, setIsOpen] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [fontToUpload, setFontToUpload] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [fontToDelete, setFontToDelete] = useState(null);
  const fontUploadRef = useRef(null);

  const handleDeleteFont = async (font) => {
    setIsDeleteModalOpen(false);
    if (!font || !font.key) return;
    await dispatch(deleteUserFont(font));
  };

  const handleFontUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedExts = ["ttf", "otf", "woff", "woff2"];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!allowedExts.includes(ext)) {
      setUploadError("Unsupported format. Use TTF, OTF, WOFF, or WOFF2.");
      if (fontUploadRef.current) fontUploadRef.current.value = "";
      return;
    }
    setUploadError("");
    setFontToUpload(file);
    setIsUploadModalOpen(true);
  };

  const handleCancelUpload = () => {
    setIsUploadModalOpen(false);
    setFontToUpload(null);
    if (fontUploadRef.current) fontUploadRef.current.value = "";
  };

  const handleConfirmUpload = async (file) => {
    setIsUploadModalOpen(false);
    setIsUploading(true);
    try {
      const result = await dispatch(uploadFontToS3(file));
      if (result?.success) {
        toast(
          <CustomToast title="" content={"Font uploaded successfully !"} />,
          customToatStyle,
        );
        onFontUploaded?.(result.fontFamily);
      } else {
        setUploadError(result?.error || "Upload failed. Please try again.");
      }
    } catch {
      setUploadError("Upload failed. Please try again.");
    }
    setIsUploading(false);
    setFontToUpload(null);
    if (fontUploadRef.current) fontUploadRef.current.value = "";
  };

  return (
    <div className="font-upload-section">
      {/* Section header */}
      <button
        type="button"
        className={`cf-head${isOpen ? " cf-head--open" : ""}`}
        onClick={() => setIsOpen((o) => !o)}
      >
        
        <div className="cf-title inline-block group cursor-help underline-offset-4">
            <span className="cursor-help">Custom Fonts</span>
            <span className="absolute hidden group-hover:block cursor-help ml-[-210px] mt-[-30px]  p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-center">
              Upload and manage custom font files
            </span>
          </div>
        <span className="cf-right">
          {!isOpen && (
            <span className="cf-count">
              {s3CustomFonts.length}{" "}
              {s3CustomFonts.length === 1 ? "font" : "fonts"}
            </span>
          )}
          <svg
            className="cf-chevron"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d={isOpen ? "m18 15-6-6-6 6" : "m6 9 6 6 6-6"} />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="cf-body">
          {/* Dropzone (label wraps the hidden file input) */}
          <label
            className={`cf-dropzone${isUploading ? " cf-dropzone--loading" : ""}`}
          >
            {isUploading ? (
              <>
                <span className="font-upload-spinner" />
                <span className="cf-dz-title">Uploading…</span>
              </>
            ) : (
              <>
                <svg
                  className="cf-dz-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span className="cf-dz-title">Click or drag font file</span>
                <span className="cf-dz-sub">Supports TTF, OTF, WOFF</span>
              </>
            )}
            <input
              ref={fontUploadRef}
              type="file"
              accept=".ttf,.otf,.woff,.woff2"
              onChange={handleFontUpload}
              className="font-upload-input"
              disabled={isUploading}
            />
          </label>

          {uploadError && <p className="font-upload-error">{uploadError}</p>}

          {s3CustomFonts.length > 0 && (
            <div className="cf-list">
              {s3CustomFonts.map((font) => (
                <div key={font.fontFamily} className="cf-row">
                  <div className="cf-row-left">
                    <span className="cf-badge">Ag</span>
                    <span
                      className="cf-name"
                      style={{ fontFamily: `'${font.fontFamily}'` }}
                      title={font.fontFamily}
                    >
                      {font.fontFamily}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="cf-del"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFontToDelete(font);
                      setIsDeleteModalOpen(true);
                    }}
                    title="Delete font"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isDeleteModalOpen && (
        <DeleteFontModal
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          fontToDelete={fontToDelete}
          handleDeleteFont={handleDeleteFont}
        />
      )}

      {isUploadModalOpen && (
        <UploadFontModal
          handleCancelUpload={handleCancelUpload}
          fontToUpload={fontToUpload}
          handleConfirmUpload={handleConfirmUpload}
        />
      )}
    </div>
  );
};

export default CustomFontUpload;